import { useState, useCallback, useEffect } from "react";
import {
  Node,
  NodeProps,
  NodeResizer,
  NodeToolbar,
  Position,
  ResizeDragEvent,
  ResizeParams,
} from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import classNames from "classnames";
import { Colors, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import {
  isViewOnlyModeAtom,
  isOnlyNodeSelectedAtomFamily,
  isMultiSelectedAtomFamily,
  shouldShowStepIdsAtom,
  saveNodeOnResizeEndSetter,
  areHandlesVisibleAtomFamily,
  EmbedNodeData,
} from "@zapier/canvas-state";
import { isImageUrl } from "@zapier/rich-text-editor";
import { DEFAULT_NODE_WIDTH } from "@zapier/canvas-constants";

import { useDeleteNodes } from "../../../../hooks/useDeleteNodes";
import { ExpandIcon } from "../../../icons/ExpandIcon";
import { Handles } from "../../../Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { StepId } from "../../../StepId";
import { ButtonOrLinkWithTooltip } from "../../../ButtonOrLinkWithTooltip";
import { NodeToolbarBottom } from "../../../FloatingInlineToolbar/NodeToolbarBottom";
import { EmbedContentModal } from "../../../Embeds";
import { isValidVideoUrl } from "../../../Embeds/VideoPlayer";
import { DynamicNodeLinkDisplay } from "../../../NodeLinkDisplay";
import { getNodeResizerHandleStyle, getNodeResizerLineStyle } from "../utils";
import { EmbedNodeLinkInput } from "./EmbedNodeLinkInput";
import { EmbedNodeContent } from "./EmbedNodeContent";
import styles from "./EmbedNode.module.css";

type EmbedNodeToolbarProps = {
  link?: string;
  activeButton: string | null;
  onDelete: () => void;
  onOpenLink: () => void;
  onMaximize: () => void;
  onToggleEdit: () => void;
};

function EmbedNodeToolbar({
  link,
  activeButton,
  onDelete,
  onOpenLink,
  onMaximize,
  onToggleEdit,
}: EmbedNodeToolbarProps) {
  return (
    <NodeToolbar position={Position.Top} offset={15} className="nopan">
      <div className={styles.floatingToolbarWrapper}>
        <ButtonOrLinkWithTooltip
          content="Delete"
          onClick={onDelete}
          className={classNames(styles.buttonNoStyle, {
            [styles.activeButton]: activeButton === "delete",
          })}
        >
          <Icon name="actionTrash" size={20} />
        </ButtonOrLinkWithTooltip>
        <ButtonOrLinkWithTooltip
          content="Open link in new tab"
          onClick={link ? onOpenLink : () => {}}
          disabled={!link}
          className={classNames(styles.buttonNoStyle, {
            [styles.activeButton]: activeButton === "open",
            [styles.buttonDisabled]: !link,
          })}
        >
          <Icon name="arrowOffsite" size={20} />
        </ButtonOrLinkWithTooltip>
        <ButtonOrLinkWithTooltip
          content="Maximize"
          onClick={link ? onMaximize : () => {}}
          disabled={!link}
          className={classNames(styles.buttonNoStyle, {
            [styles.activeButton]: activeButton === "maximize",
            [styles.buttonDisabled]: !link,
          })}
        >
          <ExpandIcon width="20" height="20" fill={Colors.TextInverted} />
        </ButtonOrLinkWithTooltip>
        <ButtonOrLinkWithTooltip
          content={link ? "Edit link" : "Add link"}
          onClick={onToggleEdit}
          className={classNames(styles.buttonNoStyle, styles.noDivider, {
            [styles.activeButton]: activeButton === "edit",
          })}
        >
          <Icon name="actionLink" size={20} />
        </ButtonOrLinkWithTooltip>
      </div>
    </NodeToolbar>
  );
}

export function EmbedNode(props: NodeProps<Node<EmbedNodeData>>) {
  const {
    id,
    dragging,
    data: { link },
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeButton, setActiveButton] = useState<string | null>(null);

  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));
  const saveOnResizeEnd = useSetAtom(saveNodeOnResizeEndSetter);
  const { onNodesDelete } = useDeleteNodes();

  // Dragging is only true if the node is the only one being dragged (false when multiple nodes are being dragged)
  const isNodeSelectedOrDragged = !isViewOnlyMode && (dragging || isSelected);

  const handleResizeEnd = useCallback(
    (_e: ResizeDragEvent, { width, height, x, y }: ResizeParams) => {
      saveOnResizeEnd({ id, width, height, x, y });
    },
    [saveOnResizeEnd, id],
  );

  const toggleEdit = useCallback(() => {
    if (!isViewOnlyMode) {
      if (isEditing) {
        // Toggle off - close editing
        setIsEditing(false);
        setActiveButton(null);
      } else {
        // Toggle on - start editing
        setIsEditing(true);
        setActiveButton("edit");
      }
    }
  }, [isViewOnlyMode, isEditing]);

  const handleLinkInputClose = useCallback(() => {
    setIsEditing(false);
    setActiveButton(null);
  }, []);

  const handleOpenLink = useCallback(() => {
    if (link) {
      setActiveButton("open");
      window.open(link, "_blank", "noopener,noreferrer");
      // Reset active state after a brief moment
      setTimeout(() => setActiveButton(null), 150);
    }
  }, [link]);

  const handleMaximize = useCallback(() => {
    setActiveButton("maximize");
    setIsModalOpen(true);
    setIsEditing(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveButton(null);
  }, []);

  const handleDelete = useCallback(() => {
    setActiveButton("delete");
    onNodesDelete([{ id, position: { x: 0, y: 0 }, data: {} }]);
  }, [onNodesDelete, id]);

  // Close editing state when node is deselected
  useEffect(() => {
    if (!isSelected && isEditing) {
      setIsEditing(false);
      setActiveButton(null);
    }
  }, [isSelected, isEditing]);

  // Check if the embed content is a video or image to enable aspect ratio
  const isVideoContent = link ? isValidVideoUrl(link) : false;
  const isImageContent = link ? isImageUrl(link) : false;
  const shouldKeepAspectRatio = isVideoContent || isImageContent;

  const isToolbarVisible = !isViewOnlyMode && isSelected;

  return (
    <>
      {isToolbarVisible && (
        <EmbedNodeToolbar
          link={link}
          activeButton={activeButton}
          onDelete={handleDelete}
          onOpenLink={handleOpenLink}
          onMaximize={handleMaximize}
          onToggleEdit={toggleEdit}
        />
      )}
      <NodeHoverWrapper>
        <div className={styles.container}>
          {shouldShowStepIds && <StepId />}
          <Handles nodeType="embed" isVisible={areHandlesVisible} />
          <NodeResizer
            minWidth={DEFAULT_NODE_WIDTH}
            minHeight={link ? undefined : 120}
            keepAspectRatio={shouldKeepAspectRatio}
            handleStyle={getNodeResizerHandleStyle()}
            lineStyle={getNodeResizerLineStyle()}
            isVisible={isNodeSelectedOrDragged}
            onResizeEnd={handleResizeEnd}
          />
          {link ? (
            <div
              className={classNames(styles.wrapper, {
                [styles.wrapperMultiSelected]: isMultiSelected,
              })}
            >
              <EmbedNodeContent url={link} />
              <DynamicNodeLinkDisplay
                nodeId={id}
                link={link}
                isViewOnlyMode={isViewOnlyMode}
                onLinkClick={toggleEdit}
              />
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Icon name="actionLink" size="large" color="GrayWarm7" />
              <Text type="ParagraphHeader1" color="GrayWarm7">
                Waiting for the perfect link...
              </Text>
            </div>
          )}
        </div>
      </NodeHoverWrapper>
      {link && (
        <EmbedContentModal
          url={link}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
      {isEditing && (
        <NodeToolbarBottom>
          <EmbedNodeLinkInput link={link} onClose={handleLinkInputClose} />
        </NodeToolbarBottom>
      )}
    </>
  );
}
